'use client';

import { useQuery } from '@tanstack/react-query';

import { isAtLeastTier } from '@/features/pricing/constants/plan-limits';

import { useAppStore } from '@/shared/contexts/app-wrapper/providers/app-provider';
import { getCurrentPlan } from '@/shared/services/payment';
import { PlanType } from '@/shared/services/payment/payment.types';

import { GET_CURRENT_PLAN_QUERY_KEY } from '@/utils/constants';

export function useCurrentPlan() {
	const selectedWorkspaceId = useAppStore((state) => state.selectedWorkspaceId);
	const currentUserRole = useAppStore((state) => state.selectedWorkspace?.currentUserRole);
	const isReferrer = currentUserRole === 'REFERRER';

	const { data: currentPlan, isLoading: isCurrentPlanLoading } = useQuery({
		queryKey: [GET_CURRENT_PLAN_QUERY_KEY, selectedWorkspaceId],
		queryFn: () => getCurrentPlan({ page_id: selectedWorkspaceId ?? undefined }),
		enabled: !!selectedWorkspaceId,
		staleTime: 5 * 60 * 1000,
	});

	const planType = currentPlan?.planType;

	// Business-tier features are also unlocked for Pro. Defaults to `true` while the
	// plan loads so a paying user never sees a gate flash before the answer arrives.
	const hasBusinessFeatures = isCurrentPlanLoading
		? true
		: isAtLeastTier(planType, 'business');
	const isFreePlan = isCurrentPlanLoading ? false : planType === PlanType.FREE;
	const isStarterPlan =
		!isCurrentPlanLoading &&
		(planType === PlanType.PRO_MONTHLY || planType === PlanType.PRO_YEARLY);

	const isOverdue = (currentPlan?.isOverdue && !isReferrer) ?? false;

	return {
		currentPlan,
		planType,
		/**
		 * Exposed so callers that reveal something while unlocked can fail closed until
		 * the plan is known. `isFreePlan` is `false` while loading, so a gate written as
		 * `isLocked={isFreePlan}` is briefly OPEN on every cold load.
		 */
		isCurrentPlanLoading,
		hasBusinessFeatures,
		/** @deprecated Use `hasBusinessFeatures` — this name excludes Pro by implication. */
		isBusinessPlan: hasBusinessFeatures,
		isOverdue,
		isFreePlan,
		isStarterPlan,
	};
}
